// src/pages/PrivacyPolicy.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';

function PrivacyPolicy() {
  const { salonId } = useParams();
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClientData = async () => {
      if (!salonId) {
        setLoading(false);
        return;
      }
      try {
        const clientDocRef = doc(db, 'salons', salonId);
        const docSnap = await getDoc(clientDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setClientName(data.name || 'Our Salon');
          setClientEmail(data.email || '');
        }
      } catch (err) {
        console.error('Error fetching client data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClientData();
  }, [salonId]);

  const handleBackToLogin = () => {
    window.location.href = `/BespokeBeauty/${salonId || 'demo-salon'}/customer/login`;
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      lineHeight: '1.6',
      color: '#333'
    }}>
      <div style={{
        borderBottom: '2px solid #E64C31',
        paddingBottom: '20px',
        marginBottom: '30px'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={handleBackToLogin}
            style={{
              background: '#E64C31',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            &larr; Back to Login
          </button>
        </div>
        <h1 style={{ color: '#E64C31', margin: '0 0 10px 0' }}>Privacy Policy</h1>
        <p style={{ color: '#666', margin: '0', fontSize: '14px' }}>Effective Date: December 1, 2024</p>
      </div>

      <div>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>1. Introduction</h2>
          <p>
            {clientName} ("we", "us", or "our") is committed to protecting your privacy and personal data
            in compliance with Singapore's Personal Data Protection Act 2012 (PDPA). This Privacy Policy
            explains how we collect, use, disclose, and protect your personal data when you use the
            BespokeBeauty platform.
          </p>
          <p>
            <strong>Important:</strong> BespokeBeauty is a software platform developed by APT Bespoke Pte Ltd
            ("Platform Provider"). APT Bespoke Pte Ltd does not control or have access to your personal data -
            all data collection and processing is performed by {clientName}.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>2. Data Controller</h2>
          <p>
            {clientName} is the data controller responsible for your personal data. For data protection
            inquiries, contact us at:
          </p>
          <div style={{
            background: '#f5f5f5',
            padding: '15px',
            borderRadius: '5px',
            margin: '15px 0'
          }}>
            <p><strong>{clientName} Data Protection Officer</strong></p>
            <p>Email: {clientEmail}</p>
          </div>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>3. Personal Data We Collect</h2>

          <h3 style={{ color: '#5B8FA8' }}>3.1 Account and Contact Information</h3>
          <ul>
            <li>Full name and preferred name</li>
            <li>Email address (for transactional and marketing communications)</li>
            <li>Phone number</li>
            <li>Month of birth (for birthday promotions and age-appropriate services)</li>
            <li>Address postal code (for location-based offers and service areas)</li>
            <li>Profile photograph (optional)</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>3.2 Service Preference Data</h3>
          <ul>
            <li>Preferred stylists for personalized booking experience</li>
            <li>Preferred appointment days and times</li>
            <li>Service preferences and treatment history</li>
            <li>Special requirements or notes</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>3.3 Appointment and Service Data</h3>
          <ul>
            <li>Complete booking history and appointment patterns</li>
            <li>Service selections and treatment notes</li>
            <li>Payment information (processed through secure payment providers)</li>
            <li>Feedback and reviews</li>
            <li>No-show and cancellation history</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>3.4 Loyalty and Engagement Data</h3>
          <ul>
            <li>Rewards points balance and loyalty program participation</li>
            <li>Campaign responses and engagement preferences</li>
            <li>Promotional offer usage and redemption history</li>
            <li>Communication interaction data</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>3.5 Communication Tokens and Preferences</h3>
          <ul>
            <li>Email marketing preferences and consent status</li>
            <li>Cloud Messaging tokens for push notifications (via Google Firebase)</li>
            <li>SMS/push notification preferences</li>
            <li>Communication history and delivery status</li>
            <li>Unsubscribe requests and preferences</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>3.6 AI Enhancement Data</h3>
          <ul>
            <li>Interaction data with our BespokeAI system</li>
            <li>Query patterns and response preferences</li>
            <li>Service recommendation feedback</li>
            <li>Automated booking assistance usage</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>3.7 Technical Data</h3>
          <ul>
            <li>Device information and browser type</li>
            <li>IP address and general location data</li>
            <li>Platform usage patterns and interactions</li>
            <li>Login timestamps and session data</li>
            <li>Google Firebase analytics data</li>
          </ul>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>4. How We Collect Your Data</h2>
          <p>We collect your personal data through:</p>
          <ul>
            <li><strong>Direct provision:</strong> Information you provide when creating an account, booking appointments, or updating your profile</li>
            <li><strong>Automatic collection:</strong> Technical data collected through your use of the platform via Google Firebase services</li>
            <li><strong>Third-party sources:</strong> Social media login providers (Google, Facebook, Apple) with your consent</li>
            <li><strong>In-salon interactions:</strong> Data collected during your visits to our physical location</li>
            <li><strong>Device permissions:</strong> Cloud Messaging tokens collected when you grant push notification permissions</li>
            <li><strong>AI interactions:</strong> Data generated through your use of BespokeAI features for service recommendations</li>
            <li><strong>Communication responses:</strong> Engagement data from email campaigns and promotional activities</li>
          </ul>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>5. How We Use Your Data</h2>
          <p>We process your personal data for the following purposes:</p>

          <h3 style={{ color: '#5B8FA8' }}>5.1 Service Provision (Contract Performance)</h3>
          <ul>
            <li>Creating and managing your account</li>
            <li>Processing appointment bookings and payments</li>
            <li>Matching you with preferred stylists and appointment times</li>
            <li>Providing customer support and service history</li>
            <li>Managing loyalty program benefits and rewards points</li>
            <li>Delivering location-based services using postal code data</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>5.2 Transactional Communication (Contract Performance)</h3>
          <ul>
            <li>Sending appointment confirmations and reminders via email</li>
            <li>Providing service updates and important account notifications</li>
            <li>Delivering real-time appointment updates via push notifications</li>
            <li>Responding to inquiries and providing customer support</li>
            <li>Processing loyalty program milestone notifications</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>5.3 Marketing Communication (Consent)</h3>
          <ul>
            <li>Sending promotional offers and newsletters via email</li>
            <li>Delivering birthday month special offers using birth month data</li>
            <li>Sending loyalty program promotional notifications via push notifications</li>
            <li>Providing service recommendations based on preferences and history</li>
            <li>Delivering targeted campaigns based on engagement responses</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>5.4 Service Enhancement (Legitimate Interest)</h3>
          <ul>
            <li>Analyzing booking patterns to improve service availability</li>
            <li>Training and improving our BespokeAI recommendation system</li>
            <li>Personalizing user experience based on preferences and history</li>
            <li>Measuring campaign effectiveness and customer engagement</li>
            <li>Improving platform functionality and user interface</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>5.5 Legal Compliance</h3>
          <ul>
            <li>Maintaining records as required by Singapore law</li>
            <li>Responding to legal requests and investigations</li>
            <li>Protecting against fraud and security threats</li>
            <li>Processing unsubscribe requests within legal timeframes</li>
          </ul>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>6. Data Sharing and Disclosure</h2>
          <p>We may share your personal data with:</p>

          <h3 style={{ color: '#5B8FA8' }}>6.1 Google Firebase Services</h3>
          <ul>
            <li>Google Firebase for cloud hosting and data storage (servers located in Singapore)</li>
            <li>Google Firebase Cloud Messaging for push notification delivery</li>
            <li>Google Firebase Authentication for secure login services</li>
            <li>Google Analytics for platform usage insights (anonymized data only)</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>6.2 Service Providers</h3>
          <ul>
            <li>Payment processors for secure transaction processing</li>
            <li>Email service providers for marketing and transactional communications</li>
            <li>SMS service providers for text message notifications</li>
            <li>Customer support tools for service delivery</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>6.3 Platform Provider</h3>
          <p>
            APT Bespoke Pte Ltd may access aggregated, anonymized data for platform
            improvement purposes only. They do not have access to your identifiable personal data
            unless required for technical support or system maintenance.
          </p>

          <h3 style={{ color: '#5B8FA8' }}>6.4 Legal Requirements</h3>
          <p>
            We may disclose your data when required by Singapore law, court orders,
            or government regulations.
          </p>

          <p><strong>We never sell your personal data to third parties.</strong></p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>7. Communication Rights and Unsubscription</h2>

          <h3 style={{ color: '#5B8FA8' }}>7.1 Our Communication Rights</h3>
          <p>
            {clientName} reserves the right to send communications to customers according
            to their stated communication preferences, including:
          </p>
          <ul>
            <li>Transactional emails and push notifications for service delivery</li>
            <li>Marketing communications based on your consent preferences</li>
            <li>Emergency service notifications and important account updates</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>7.2 Your Unsubscription Rights</h3>
          <p>
            You reserve the right to unsubscribe from marketing communications at any time through:
          </p>
          <ul>
            <li>Application settings in your customer profile</li>
            <li>Unsubscribe links in email communications</li>
            <li>Push notification settings in your device</li>
            <li>Direct communication with our customer service</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>7.3 Processing Timeline</h3>
          <p>
            {clientName} agrees to honor unsubscription requests within 7 business days
            of receiving such indication. During this period, you may receive previously
            scheduled communications.
          </p>

          <p style={{
            background: '#FFE5E1',
            padding: '15px',
            borderRadius: '5px',
            borderLeft: '4px solid #E64C31'
          }}>
            <strong>Important:</strong> Transactional communications (appointment confirmations,
            account security alerts) cannot be unsubscribed from as they are essential for
            service delivery and account security.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>8. Data Retention</h2>
          <p>We retain your personal data for the following periods:</p>
          <ul>
            <li><strong>Active accounts:</strong> As long as your account remains active</li>
            <li><strong>Appointment and service records:</strong> 3 years after your last appointment</li>
            <li><strong>Marketing communication data:</strong> Until you withdraw consent or 2 years of inactivity</li>
            <li><strong>Cloud Messaging tokens:</strong> Until device uninstalls app or revokes permissions</li>
            <li><strong>BespokeAI interaction data:</strong> 2 years for service improvement purposes</li>
            <li><strong>Loyalty and rewards data:</strong> 3 years after account closure</li>
            <li><strong>Payment records:</strong> 7 years as required by Singapore tax law</li>
            <li><strong>Unsubscribe requests:</strong> Permanent record to respect your preferences</li>
            <li><strong>Legal compliance data:</strong> As required by applicable laws</li>
          </ul>
          <p>
            After account termination, your personal data will be securely deleted
            within 30 days from Google Firebase servers, except where retention is required by law
            or for legitimate business purposes (such as honoring unsubscribe requests).
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>9. Your Rights Under PDPA</h2>
          <p>Under Singapore's PDPA, you have the following rights:</p>

          <h3 style={{ color: '#5B8FA8' }}>9.1 Access Right</h3>
          <p>Request access to your personal data we hold and how it's being used.</p>

          <h3 style={{ color: '#5B8FA8' }}>9.2 Correction Right</h3>
          <p>Request correction of inaccurate or incomplete personal data.</p>

          <h3 style={{ color: '#5B8FA8' }}>9.3 Withdrawal of Consent</h3>
          <p>Withdraw your consent for marketing communications at any time.</p>

          <h3 style={{ color: '#5B8FA8' }}>9.4 Objection Right</h3>
          <p>Object to processing of your data for direct marketing purposes.</p>

          <p style={{
            background: '#FFE5E1',
            padding: '15px',
            borderRadius: '5px',
            borderLeft: '4px solid #E64C31'
          }}>
            <strong>How to Exercise Your Rights:</strong><br/>
            Email us at {clientEmail} or contact us through the platform.
            We will respond within 30 days as required by PDPA.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>10. Data Security</h2>
          <p>We implement appropriate security measures to protect your personal data:</p>
          <ul>
            <li><strong>Google Firebase Security:</strong> Enterprise-grade security provided by Google's cloud infrastructure</li>
            <li><strong>Encryption:</strong> Data is encrypted in transit and at rest using industry-standard protocols</li>
            <li><strong>Access controls:</strong> Limited access on a need-to-know basis with multi-factor authentication</li>
            <li><strong>Regular audits:</strong> Security assessments and vulnerability testing of our systems</li>
            <li><strong>Staff training:</strong> Regular privacy and security training for all salon staff</li>
            <li><strong>Incident response:</strong> Procedures for handling data breaches and security incidents</li>
            <li><strong>Token security:</strong> Secure handling of Cloud Messaging tokens and authentication data</li>
          </ul>
          <p>
            In the event of a data breach that may cause harm, we will notify affected
            individuals and the Personal Data Protection Commission within 72 hours.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>11. International Data Transfers</h2>
          <p>
            Your personal data is stored and processed on Google Firebase servers located
            in Singapore. If we transfer data outside Singapore for specific services,
            we ensure adequate protection through:
          </p>
          <ul>
            <li>Google's global data protection standards and certifications</li>
            <li>Adequacy decisions by Singapore authorities</li>
            <li>Standard contractual clauses with service providers</li>
            <li>Certified data protection frameworks</li>
          </ul>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>12. Children's Privacy</h2>
          <p>
            Our services are intended for individuals aged 15 and above. For customers
            under 18, we may require parental consent for certain services and marketing
            communications. We do not knowingly collect personal data from children under 15
            without parental consent.
          </p>
          <p>
            Parents or guardians can contact us to review, update, or delete their minor
            child's personal data at any time.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>13. Cookies and Tracking</h2>
          <p>We use cookies and similar technologies through Google Firebase services to:</p>
          <ul>
            <li>Maintain your login session and authentication state</li>
            <li>Remember your preferences and settings</li>
            <li>Analyze platform usage and performance</li>
            <li>Improve user experience and platform functionality</li>
            <li>Deliver personalized content and recommendations</li>
            <li>Measure the effectiveness of marketing campaigns</li>
          </ul>
          <p>
            You can control cookies through your browser settings. Disabling certain
            cookies may affect platform functionality, including login and notification features.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>14. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy to reflect changes in our practices or
            legal requirements. Material changes will be communicated through:
          </p>
          <ul>
            <li>Email notifications to registered users (if you haven't unsubscribed)</li>
            <li>Push notifications for critical privacy changes</li>
            <li>Prominent notices on the platform</li>
            <li>Updated "Effective Date" at the top of this policy</li>
          </ul>
          <p>
            Your continued use of our services after policy changes constitutes acceptance
            of the updated terms.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>15. Contact Information</h2>
          <p>For privacy-related inquiries or to exercise your rights, contact:</p>

          <div style={{
            background: '#f5f5f5',
            padding: '15px',
            borderRadius: '5px',
            margin: '15px 0'
          }}>
            <p><strong>{clientName} Data Protection Officer</strong></p>
            <p>Email: {clientEmail}</p>
          </div>

          <p style={{ fontSize: '14px', color: '#666' }}>
            For technical platform issues, contact APT Bespoke Pte Ltd at: support@bespokecrm.cloud
          </p>

          <p style={{ fontSize: '14px', color: '#666' }}>
            If you have concerns about our data handling practices, you may also contact
            the Personal Data Protection Commission Singapore at: <a href="https://www.pdpc.gov.sg">www.pdpc.gov.sg</a>
          </p>
        </div>

        <div style={{
          borderTop: '1px solid #ddd',
          paddingTop: '20px',
          textAlign: 'center',
          fontSize: '14px',
          color: '#666'
        }}>
          <p>
            Last updated: December 1, 2024<br/>
            Version: 1.0<br/>
            This policy complies with Singapore's Personal Data Protection Act 2012
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
